import React, { useState } from "react";
import { Box, Theme, Typography, useMediaQuery } from "@mui/material";
import styled from "@emotion/styled";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import { useTranslation } from "react-i18next";
import { format, getUnixTime } from "date-fns";
import { Invoice, InvoiceList } from "@rentablo/api/apiv1";
import { LoadingPlaceHolder } from "@rentablo/components";
import { useDownload } from "@hooks";
import { TaxHelper } from "@features/cashback/components/CashbackSummary/TaxHelper/TaxHelper";
import { CashbackBonusBanner } from "@features/cashback/CashbackBonusBanner";
import { CashbackSummaryRowDesktop } from "./CashbackSummary/CashbackSummaryRowDesktop";
import { CashbackSummaryRowMobile } from "./CashbackSummary/CashbackSummaryRowMobile";
import { DownloadControl } from "./CashbackSummary/DownloadControl";
import { CashbackDashboardBillingDetailsDialog } from "./CashbackDashboardBillingDetailsDialog";

const Container = styled.div`
  width: 100%;
`;

const Header = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const TableWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing(0)};
  border-color: ${({ theme }) => theme.palette.border.secondary};
  border-style: solid;
  border-width: 0;
  border-bottom-width: 1px;
  td {
    border-bottom-width: 0;
  }
  ${({ theme }) => theme.breakpoints.down("md")} {
    border-bottom-width: 0;
  }

  tr:last-child td {
    padding-bottom: ${({ theme }) => theme.spacing(6)};
  }
`;

const BillingCycleHeader = styled.div`
  display: flex;
  padding-top: ${({ theme }) => theme.spacing(6)};
  justify-content: space-between;
`;

// Stub for invoice object
export interface CashbackDashboardSummaryProps {
  invoiceList?: InvoiceList;
  showHeader?: boolean;
  isFetching?: boolean;
}

export const CashbackDashboardSummary = (
  props: CashbackDashboardSummaryProps
) => {
  const { t } = useTranslation();
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const { invoiceList, showHeader = false, isFetching } = props;
  const { mutate: download } = useDownload("application/pdf", "Rechnung");
  const [activeInvoiceTaxData, setActiveInvoiceTaxData] = useState<
    { id: number; date?: string } | undefined
  >();

  const incompleteList = {
    invoices: invoiceList?.invoices?.filter(
      (invoice) => !invoice?.billingCycleId
    ),
  };
  const regularList = {
    invoices: invoiceList?.invoices?.filter(
      (invoice) => invoice?.billingCycleId
    ),
  };

  const handleDownload = (id: number, filename: string) =>
    download({ url: `/billingCycles/${id}/invoices?format=pdf`, filename });
  const date = (inv?: Invoice) => {
    return inv?.billingCycleCreationTimestamp
      ? format(new Date(inv.billingCycleCreationTimestamp), "dd.MM.yyyy")
      : "";
  };
  const [open, setOpen] = React.useState(false);
  const [ids, setIds] = React.useState<number[]>([]);

  const handleClickOpen = (ids: number[]) => {
    setIds(ids);
    setOpen(true);
  };

  const handleClose = () => {
    setIds([]);
    setOpen(false);
  };

  const invoiceSorter = (a: Invoice, b: Invoice) =>
    getUnixTime(new Date(b.billingCycleStartDate!)) -
    getUnixTime(new Date(a.billingCycleStartDate!));

  const onTaxHelperOpen = (
    accountHolderId: number,
    billingCycleCreationTimestamp?: string
  ) => {
    setActiveInvoiceTaxData({
      id: accountHolderId,
      date: billingCycleCreationTimestamp,
    });
  };

  if (activeInvoiceTaxData) {
    return (
      <TaxHelper
        accountHolderId={activeInvoiceTaxData.id}
        date={activeInvoiceTaxData.date}
        onBackClick={() => setActiveInvoiceTaxData(undefined)}
      />
    );
  }

  return (
    <Container>
      <CashbackBonusBanner />
      <CashbackDashboardBillingDetailsDialog
        open={open}
        selectedValue={ids}
        onClose={handleClose}
      />
      {showHeader && (
        <Header>
          <Typography variant="body3" color="textSecondary">
            {t("domains.cashback.dashboard.summary.lastBills")}
          </Typography>
        </Header>
      )}
      {isFetching && (
        <Box mt={3}>
          <LoadingPlaceHolder />
        </Box>
      )}
      {incompleteList?.invoices?.map((inv, idx) => (
        <div key={idx}>
          <BillingCycleHeader>
            {inv?.billingCycleId && (
              <Box display="flex" flexDirection="column">
                <Typography variant="body1" fontWeight="600">
                  {t("domains.cashback.dashboard.summary.accountingFrom")}
                  {date(inv)}
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  fontWeight="600"
                >
                  {`${t("domains.cashback.dashboard.summary.period")} ${format(
                    new Date(inv!.billingCycleStartDate!),
                    "dd.MM.YYY"
                  )} - ${format(
                    new Date(inv!.billingCycleEndDate!),
                    "dd.MM.YYY"
                  )}`}
                </Typography>
              </Box>
            )}
            {!inv?.billingCycleId && (
              <Typography variant="body3" color="textSecondary">
                {t("domains.cashback.dashboard.summary.notBilledYet")}
              </Typography>
            )}
            {!isSm && (
              <DownloadControl handleDownload={handleDownload} invoice={inv} />
            )}
          </BillingCycleHeader>
          <TableWrapper>
            <Table>
              <TableBody>
                {inv.invoiceItems?.map((invoiceItem, idx) =>
                  !isSm ? (
                    <CashbackSummaryRowDesktop
                      showInvoice={handleClickOpen}
                      key={idx}
                      billingCycleId={inv?.billingCycleId}
                      invoiceItem={invoiceItem}
                      onTaxHelperOpen={(id: number) => {
                        onTaxHelperOpen(id, inv.billingCycleCreationTimestamp);
                      }}
                    />
                  ) : (
                    <CashbackSummaryRowMobile
                      showInvoice={handleClickOpen}
                      key={idx}
                      billingCycleId={inv?.billingCycleId}
                      invoiceItem={invoiceItem}
                      onTaxHelperOpen={(id: number) => {
                        onTaxHelperOpen(id, inv.billingCycleCreationTimestamp);
                      }}
                    />
                  )
                )}
              </TableBody>
            </Table>
          </TableWrapper>
          {isSm && (
            <DownloadControl handleDownload={handleDownload} invoice={inv} />
          )}
        </div>
      ))}
      {regularList?.invoices?.sort(invoiceSorter).map((inv, idx) => (
        <div key={idx}>
          <BillingCycleHeader>
            {inv?.billingCycleId && (
              <Box display="flex" flexDirection="column">
                <Typography variant="body1" fontWeight="600">
                  {t("domains.cashback.dashboard.summary.accountingFrom")}
                  {date(inv)}
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  fontWeight="600"
                >
                  {`${t("domains.cashback.dashboard.summary.period")} ${format(
                    new Date(inv!.billingCycleStartDate!),
                    "dd.MM.YYY"
                  )} - ${format(
                    new Date(inv!.billingCycleEndDate!),
                    "dd.MM.YYY"
                  )}`}
                </Typography>
              </Box>
            )}
            {!inv?.billingCycleId && (
              <Typography variant="body3" color="textSecondary">
                {t("domains.cashback.dashboard.summary.notBilledYet")}
              </Typography>
            )}
            {!isSm && (
              <DownloadControl handleDownload={handleDownload} invoice={inv} />
            )}
          </BillingCycleHeader>
          <TableWrapper>
            <Table>
              <TableBody>
                {inv.invoiceItems?.map((invoiceItem, idx) =>
                  !isSm ? (
                    <CashbackSummaryRowDesktop
                      showInvoice={handleClickOpen}
                      key={idx}
                      billingCycleId={inv?.billingCycleId}
                      invoiceItem={invoiceItem}
                      onTaxHelperOpen={(id: number) => {
                        onTaxHelperOpen(id, inv.billingCycleCreationTimestamp);
                      }}
                    />
                  ) : (
                    <CashbackSummaryRowMobile
                      showInvoice={handleClickOpen}
                      key={idx}
                      billingCycleId={inv?.billingCycleId}
                      invoiceItem={invoiceItem}
                      onTaxHelperOpen={(id: number) => {
                        onTaxHelperOpen(id, inv.billingCycleCreationTimestamp);
                      }}
                    />
                  )
                )}
              </TableBody>
            </Table>
          </TableWrapper>
          {isSm && (
            <DownloadControl handleDownload={handleDownload} invoice={inv} />
          )}
        </div>
      ))}
    </Container>
  );
};
